import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listSuppliers } from './api';
const PAGE_LIMIT = 50;
const CATEGORIES = [
    'Glass',
    'Hardware',
    'Frames',
    'Sealants & Adhesives',
    'Consumables',
    'Tools & Equipment',
    'Other',
];
export const SupplierList = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nameFilter, setNameFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [tokenStack, setTokenStack] = useState([undefined]);
    const [pageIndex, setPageIndex] = useState(0);
    const currentToken = tokenStack[pageIndex];
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await listSuppliers({
                name: nameFilter || undefined,
                status: statusFilter || undefined,
                category: categoryFilter || undefined,
                limit: PAGE_LIMIT,
                nextToken: currentToken,
            });
            setResult(data);
        }
        catch (e) {
            setError(e.message ?? 'Failed to load suppliers');
        }
        finally {
            setLoading(false);
        }
    }, [nameFilter, statusFilter, categoryFilter, currentToken]);
    useEffect(() => {
        load();
    }, [load]);
    const handleSearch = () => {
        setTokenStack([undefined]);
        setPageIndex(0);
    };
    const handleNext = () => {
        if (!result?.nextToken)
            return;
        const next = pageIndex + 1;
        const newStack = [...tokenStack.slice(0, next), result.nextToken];
        setTokenStack(newStack);
        setPageIndex(next);
    };
    const handlePrev = () => {
        if (pageIndex === 0)
            return;
        setPageIndex(pageIndex - 1);
    };
    const statusBadge = (status) => ({
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '980px',
        fontSize: '11px',
        fontWeight: '700',
        backgroundColor: status === 'active' ? 'rgba(52,199,89,0.12)' : 'rgba(142,142,147,0.15)',
        color: status === 'active' ? '#248a3d' : '#6e6e73',
    });
    return (_jsx(Layout, { children: _jsxs("div", { children: [_jsxs("div", { style: styles.pageHeader, children: [_jsxs("div", { children: [_jsx("h2", { style: styles.heading, children: "Suppliers" }), _jsx("p", { style: styles.subheading, children: result ? `${result.count} supplier${result.count !== 1 ? 's' : ''}` : '' })] }), _jsx("button", { style: styles.newButton, onClick: () => navigate('/suppliers/new'), children: "+ New Supplier" })] }), _jsxs("div", { style: { ...styles.filterBar, flexDirection: isMobile ? 'column' : 'row' }, children: [_jsx("input", { type: "text", placeholder: "Search by name\u2026", style: styles.filterInput, value: nameFilter, onChange: (e) => setNameFilter(e.target.value), onKeyDown: (e) => e.key === 'Enter' && handleSearch() }), _jsxs("select", { style: styles.filterSelect, value: statusFilter, onChange: (e) => {
                                setStatusFilter(e.target.value);
                                setTokenStack([undefined]);
                                setPageIndex(0);
                            }, children: [_jsx("option", { value: "", children: "All Statuses" }), _jsx("option", { value: "active", children: "Active" }), _jsx("option", { value: "inactive", children: "Inactive" })] }), _jsxs("select", { style: styles.filterSelect, value: categoryFilter, onChange: (e) => {
                                setCategoryFilter(e.target.value);
                                setTokenStack([undefined]);
                                setPageIndex(0);
                            }, children: [_jsx("option", { value: "", children: "All Categories" }), CATEGORIES.map((c) => (_jsx("option", { value: c, children: c }, c)))] }), _jsx("button", { style: styles.searchButton, onClick: handleSearch, children: "Search" })] }), error && _jsx("div", { style: styles.errorBanner, children: error }), loading ? (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })) : isMobile ? (
                /* Mobile: cards */
                _jsxs("div", { style: styles.cardList, children: [result?.items.length === 0 && (_jsxs("div", { style: styles.emptyState, children: [_jsx("div", { style: styles.emptyIcon, children: "\uD83C\uDFED" }), _jsx("p", { style: styles.emptyText, children: "No suppliers found" })] })), result?.items.map((s) => (_jsxs("div", { style: styles.card, onClick: () => navigate(`/suppliers/${s.id}`), children: [_jsxs("div", { style: styles.cardTop, children: [_jsx("span", { style: styles.cardId, children: s.id }), _jsx("span", { style: statusBadge(s.status), children: s.status === 'active' ? 'Active' : 'Inactive' })] }), _jsx("p", { style: styles.cardName, children: s.name }), s.category && _jsx("p", { style: styles.cardMeta, children: s.category }), (s.contactFirstName || s.contactLastName) && (_jsx("p", { style: styles.cardMeta, children: [s.contactFirstName, s.contactLastName].filter(Boolean).join(' ') })), s.phone && _jsx("p", { style: styles.cardMeta, children: s.phone }), s.accountNumber && (_jsxs("p", { style: styles.cardMeta, children: ["Acct #", s.accountNumber] }))] }, s.id)))] })) : (
                /* Desktop: table */
                _jsx("div", { style: styles.tableWrap, children: _jsxs("table", { style: styles.table, children: [_jsx("thead", { children: _jsx("tr", { children: ['ID', 'Name', 'Category', 'Contact', 'Phone', 'Account #', 'Payment Terms', 'Status'].map((h) => (_jsx("th", { style: styles.th, children: h }, h))) }) }), _jsxs("tbody", { children: [result?.items.length === 0 && (_jsx("tr", { children: _jsx("td", { colSpan: 8, style: { ...styles.td, textAlign: 'center', color: '#86868b', padding: '40px' }, children: "No suppliers found" }) })), result?.items.map((s, i) => (_jsxs("tr", { style: {
                                            ...styles.tr,
                                            backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.015)',
                                            cursor: 'pointer',
                                        }, onClick: () => navigate(`/suppliers/${s.id}`), children: [_jsx("td", { style: { ...styles.td, fontFamily: 'monospace', fontSize: '12px', color: '#86868b' }, children: s.id }), _jsx("td", { style: { ...styles.td, fontWeight: '600', color: '#1d1d1f' }, children: s.name }), _jsx("td", { style: styles.td, children: s.category || '—' }), _jsx("td", { style: styles.td, children: [s.contactFirstName, s.contactLastName].filter(Boolean).join(' ') || '—' }), _jsx("td", { style: styles.td, children: s.phone || '—' }), _jsx("td", { style: styles.td, children: s.accountNumber || '—' }), _jsx("td", { style: styles.td, children: s.paymentTerms || '—' }), _jsx("td", { style: styles.td, children: _jsx("span", { style: statusBadge(s.status), children: s.status === 'active' ? 'Active' : 'Inactive' }) })] }, s.id)))] })] }) })), (pageIndex > 0 || result?.nextToken) && (_jsxs("div", { style: styles.pagination, children: [_jsx("button", { style: { ...styles.pageButton, opacity: pageIndex === 0 ? 0.4 : 1 }, onClick: handlePrev, disabled: pageIndex === 0, children: "\u2190 Prev" }), _jsxs("span", { style: styles.pageLabel, children: ["Page ", pageIndex + 1] }), _jsx("button", { style: { ...styles.pageButton, opacity: result?.nextToken ? 1 : 0.4 }, onClick: handleNext, disabled: !result?.nextToken, children: "Next \u2192" })] }))] }) }));
};
const styles = {
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 4px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '15px',
        color: '#86868b',
        margin: 0,
    },
    newButton: {
        padding: '10px 20px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
        whiteSpace: 'nowrap',
    },
    filterBar: {
        display: 'flex',
        gap: '10px',
        marginBottom: '20px',
        flexWrap: 'wrap',
    },
    filterInput: {
        flex: 1,
        minWidth: '160px',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '14px',
        outline: 'none',
        minHeight: '44px',
        boxSizing: 'border-box',
    },
    filterSelect: {
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '14px',
        outline: 'none',
        minHeight: '44px',
        cursor: 'pointer',
    },
    searchButton: {
        padding: '10px 20px',
        backgroundColor: '#f5f5f7',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '10px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
        color: '#1d1d1f',
    },
    errorBanner: {
        padding: '14px 18px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '80px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    card: {
        backgroundColor: 'rgba(255,255,255,0.6)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '16px',
        padding: '16px',
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    },
    cardTop: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '6px',
    },
    cardId: {
        fontSize: '11px',
        fontFamily: 'monospace',
        color: '#86868b',
        fontWeight: '600',
    },
    cardName: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 4px 0',
    },
    cardMeta: {
        fontSize: '13px',
        color: '#6e6e73',
        margin: '0 0 2px 0',
    },
    tableWrap: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '14px',
    },
    th: {
        padding: '12px 16px',
        textAlign: 'left',
        fontSize: '12px',
        fontWeight: '700',
        color: '#6e6e73',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid rgba(0,0,0,0.07)',
        backgroundColor: 'rgba(0,0,0,0.02)',
    },
    td: {
        padding: '12px 16px',
        borderBottom: '1px solid rgba(0,0,0,0.05)',
        color: '#3c3c43',
        verticalAlign: 'middle',
    },
    tr: {
        transition: 'background-color 0.1s ease',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '60px 24px',
        gap: '8px',
    },
    emptyIcon: {
        fontSize: '40px',
    },
    emptyText: {
        fontSize: '15px',
        color: '#86868b',
        margin: 0,
    },
    pagination: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '24px',
    },
    pageButton: {
        padding: '8px 20px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        color: '#1d1d1f',
        minHeight: '36px',
    },
    pageLabel: {
        fontSize: '14px',
        color: '#86868b',
        fontWeight: '500',
    },
};
//# sourceMappingURL=SupplierList.js.map